import { ApolloClient } from '@apollo/client';
import { ManagedServiceId } from '@axinom/mosaic-id-utils';
import {
  Details,
  EmptyStation,
  type FormActionData,
  GenericField,
  InfoPanel,
  MessageBar,
  type ObjectSchemaDefinition,
  Paragraph,
  Section,
  SingleLineTextField,
} from '@axinom/mosaic-ui';
import { config } from 'config';
import { Field, useFormikContext } from 'formik';
import {
  ClearForgotPasswordWebHookSecretDocument,
  type ClearForgotPasswordWebHookSecretMutation,
  type ClearForgotPasswordWebHookSecretMutationVariables,
  type IdpUserStore,
  IdpUserStoreDocument,
  type IdpUserStoreQuery,
  type IdpUserStoreQueryVariables,
  type MutationUpdateIdpUserStoreArgs,
  UpdateIdpUserStoreDocument,
  type UpdateIdpUserStoreMutation,
  type UpdateIdpUserStoreMutationVariables,
  useUpdateIdpUserStoreMutation,
} from 'generated/axauth-service';
import type { BreadcrumbResolver } from 'models/breadcrumb';
import { environmentApolloClient } from 'modules/environmentApolloClient';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { getManagedServiceAvailabilityMessage } from 'util/managedServiceUtil';
import { validateUrl } from 'util/validationUtil';
import * as Yup from 'yup';
import { ensureError } from '../../../../../common/errors/ensure-error';
import type { SubmitResponse } from './ForgotPasswordWebHook.types';

const webhookValidation = Yup.object<ObjectSchemaDefinition>({
  forgotPasswordWebhook: Yup.string()
    .required('Forgot password webhook URL is required')
    .test(
      'forgot-password-webhook-url',
      'Forgot password webhook must be a valid URL',
      validateUrl,
    ),
});
type FormData = MutationUpdateIdpUserStoreArgs['input']['patch'];

export const ForgotPasswordWebHookDetails: React.FC = () => {
  const { userStoreId, envId } = useParams<{
    userStoreId: string;
    envId: string;
  }>();
  const [data, setData] = useState<IdpUserStoreQuery>();
  const [error, setError] = useState<string>();
  const [loading, setLoading] = useState<boolean>(false);
  const [serviceStatusMessage, setServiceStatusMessage] = useState<
    string | null
  >(null);
  const envApolloClient = useRef<ApolloClient<Record<string, unknown>>>();

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);

        // Check the status of axauth-service
        const statusMessage = await getManagedServiceAvailabilityMessage(
          ManagedServiceId.AXAUTH_SERVICE,
          envId,
        );

        if (statusMessage !== null) {
          setServiceStatusMessage(statusMessage);
        } else {
          envApolloClient.current = environmentApolloClient(
            envId,
            new URL(`/graphql`, config.axAuthAdminBaseUrl).toString(),
          );

          // Get IdpUserStore data
          const idpUserStoreResult = await envApolloClient.current.query<
            IdpUserStoreQuery,
            IdpUserStoreQueryVariables
          >({
            query: IdpUserStoreDocument,
            variables: {
              id: userStoreId,
            },
            fetchPolicy: 'no-cache',
          });

          setData(idpUserStoreResult.data);
        }
      } catch (e) {
        const error = ensureError(e);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [envId, userStoreId]);

  const { actions } = useActions(envId, userStoreId);

  const [updateIdpUserStore] = useUpdateIdpUserStoreMutation({
    client: envApolloClient.current,
    fetchPolicy: 'no-cache',
  });

  const saveData = useCallback(
    async (formData: FormData): Promise<SubmitResponse> => {
      envApolloClient.current = environmentApolloClient(
        envId,
        new URL(`/graphql`, config.userServiceAdminBaseUrl).toString(),
      );
      const response = await updateIdpUserStore({
        variables: {
          input: {
            id: userStoreId,
            patch: { forgotPasswordWebhook: formData.forgotPasswordWebhook },
          },
        },
      });
      return response.data;
    },
    [envId, updateIdpUserStore, userStoreId],
  );

  const Panel: React.FC = () => {
    const { values } = useFormikContext<IdpUserStore>();
    return (
      <InfoPanel>
        <Section>
          <Paragraph title="Tenant ID">{values?.tenantId}</Paragraph>
          <Paragraph title="Environment ID">{values?.environmentId}</Paragraph>
        </Section>
      </InfoPanel>
    );
  };

  const Form: React.FC = () => {
    return (
      <>
        <Field
          name="forgotPasswordWebhook"
          label="Forgot Password Webhook URL"
          tooltipContent="A webhook that will receive the reset-OTP and handles the user's password reset process. It can be used to customize the password reset experience (i.e. send a password reset email). After the action is confirmed, the [completeEndUserPasswordReset] mutation shall be called to complete the process."
          as={SingleLineTextField}
        />
        <GenericField name="secret" label="Forgot Password Webhook Secret">
          {data?.idpUserStore?.resetPasswordWebhookSecretIsSet ? (
            <p>*************************************************</p>
          ) : (
            <p>Not Generated</p>
          )}
        </GenericField>
      </>
    );
  };

  return (
    <>
      {serviceStatusMessage ? (
        <EmptyStation title="Forgot Password Webhook">
          <MessageBar type="warning" title={serviceStatusMessage}></MessageBar>
        </EmptyStation>
      ) : (
        <Details<FormData>
          defaultTitle="Forgot Password Webhook"
          subtitle="Forgot Password Webhook Details"
          actions={actions}
          validationSchema={webhookValidation}
          initialData={{
            data: data?.idpUserStore,
            loading,
            error,
          }}
          saveData={saveData}
          infoPanel={<Panel />}
        >
          <Form />
        </Details>
      )}
    </>
  );
};

function useActions(
  envId: string,
  userStoreId: string,
): {
  readonly actions: FormActionData<FormData>[];
} {
  const history = useHistory();
  const actions: FormActionData<FormData>[] = [
    {
      label: 'Webhook Secret',
      onActionSelected: () => {
        history.push(
          `/env/${envId}/services/${ManagedServiceId.AXAUTH_SERVICE}/config/userStores/${userStoreId}/forgotPassword/details/secret/forgotPassword`,
        );
      },
    },
    {
      label: 'Delete',
      confirmationMode: 'Simple',
      onActionSelected: async () => {
        const envApolloClient = environmentApolloClient(
          envId,
          new URL(`/graphql`, config.axAuthAdminBaseUrl).toString(),
        );
        await envApolloClient.mutate<
          UpdateIdpUserStoreMutation,
          UpdateIdpUserStoreMutationVariables
        >({
          mutation: UpdateIdpUserStoreDocument,
          variables: {
            input: {
              id: userStoreId,
              patch: { forgotPasswordWebhook: '' },
            },
          },
        });

        await envApolloClient.mutate<
          ClearForgotPasswordWebHookSecretMutation,
          ClearForgotPasswordWebHookSecretMutationVariables
        >({
          mutation: ClearForgotPasswordWebHookSecretDocument,
          variables: {
            input: { userStoreId: userStoreId },
          },
          fetchPolicy: 'no-cache',
        });
        history.push(
          `/env/${envId}/services/${ManagedServiceId.AXAUTH_SERVICE}/config/userStores/${userStoreId}`,
        );
      },
    },
  ];

  return { actions } as const;
}

export const ForgotPasswordWebHookDetailsBreadCrumb: BreadcrumbResolver = () =>
  'Forgot Password Webhook Details';
